<template>
  <v-container fluid>
    <v-card
      flat
      color="transparent">
      <v-toolbar
        flat
        color="white"
        rounded="lg"
        class="mb-4">
        <v-toolbar-title class="font-weight-bold">
          Warehouse Operations
        </v-toolbar-title>
        <v-divider
          class="mx-4"
          inset
          vertical></v-divider>
        <v-spacer></v-spacer>
        <v-select
          v-model="selectedDepartment"
          :items="departmentOptions"
          label="แผนก"
          prepend-inner-icon="mdi-office-building"
          outlined
          dense
          hide-details
          class="mr-2"
        ></v-select>
        <v-menu
          v-model="dateMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="dateRangeText"
              label="เลือกช่วงวันที่"
              prepend-inner-icon="mdi-calendar"
              readonly
              outlined
              dense
              hide-details
              v-bind="attrs"
              v-on="on"
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="dates"
            range
            no-title
            scrollable
            color="primary"
            @input="handleDateChange"
          >
            <v-spacer></v-spacer>
            <v-btn
              text
              color="primary"
              @click="dateMenu = false">
              Cancel
            </v-btn>
            <v-btn
              text
              color="primary"
              @click="confirmDate">
              OK
            </v-btn>
          </v-date-picker>
        </v-menu>
      </v-toolbar>
      <v-row>
        <!-- KPI Cards -->
        <v-col
          v-for="(kpi, i) in kpiCards"
          :key="i"
          cols="12"
          md="4">
          <v-card
            rounded="lg"
            class="pa-4"
            elevation="1">
            <v-list-item three-line>
              <v-list-item-content>
                <div class="overline mb-4">
                  {{ kpi.title }}
                </div>
                <v-list-item-title class="headline mb-1 font-weight-black">
                  {{ kpi.value }}
                </v-list-item-title>
                <v-list-item-subtitle>{{ kpi.subtitle }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-avatar
                size="60"
                :color="kpi.color + ' lighten-4'">
                <v-icon
                  size="30"
                  :color="kpi.color">
                  {{ kpi.icon }}
                </v-icon>
              </v-avatar>
            </v-list-item>
          </v-card>
        </v-col>

        <!-- Analytics Chart -->
        <v-col cols="12">
          <v-card
            rounded="lg"
            elevation="1">
            <v-card-title>
              ประสิทธิภาพแยกตามประเภทงาน
            </v-card-title>
            <v-card-text>
              <div>
                <task-analysis-chart :chart-data="computedChartData" />
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-card>
  </v-container>
</template>

<script>
import TaskAnalysisChart from '../components/TaskAnalysisChart.js'
import WarehouseWorkTrackingProvider from '@/resources/WarehouseWorkTrackingProvider'

const provider = new WarehouseWorkTrackingProvider()

export default {
  components: { TaskAnalysisChart },
  data: () => ({
    activeTab: 0,
    loading: false,
    dateMenu: false,
    dates: [],
    selectedDepartment: 'All',
    departmentOptions: ['All', 'Inbound', 'Outbound', 'Inventory', 'Return', 'Other'],
    kpi: {
      totalTasks: 0,
      completedTasks: 0,
      workingTasks: 0,
      totalQuantity: 0,
      totalDuration: 0,
      avgPiecesPerHour: 0
    },
    taskBreakdown: []
  }),
  computed: {
    dateRangeText () {
      if (this.dates.length !== 2) return ''
      const sorted = [...this.dates].sort()
      return `${sorted[0]} ~ ${sorted[1]}`
    },
    sortedDates () {
      if (this.dates.length !== 2) return []
      return [...this.dates].sort()
    },
    kpiCards () {
      return [
        {
          title: 'Total Tasks',
          value: this.kpi.totalTasks,
          subtitle: `สำเร็จ ${this.kpi.completedTasks} / กำลังทำ ${this.kpi.workingTasks}`,
          color: 'blue',
          icon: 'mdi-check-all'
        },
        {
          title: 'Total Quantity',
          value: this.kpi.totalQuantity.toLocaleString(),
          subtitle: 'จำนวนงานที่จัดการ',
          color: 'orange',
          icon: 'mdi-package-variant'
        },
        {
          title: 'Avg Efficiency',
          value: `${this.kpi.avgPiecesPerHour} Pcs/Hr`,
          subtitle: 'ความเร็วเฉลี่ยรวม',
          color: 'green',
          icon: 'mdi-speedometer'
        }
      ]
    },
    computedChartData () {
      return {
        labels: this.taskBreakdown.map((s) => s.taskType?.name || '-'),
        datasets: [
          {
            label: 'เวลาเฉลี่ย (นาที)',
            type: 'line',
            borderColor: '#FF5252',
            backgroundColor: 'transparent',
            fill: false,
            yAxisID: 'y-axis-avg',
            data: this.taskBreakdown.map((s) => ((s.avgDuration || 0) / 60000).toFixed(2)),
            order: 1
          },
          {
            label: 'จำนวนงาน (ครั้ง)',
            backgroundColor: '#1976D2',
            yAxisID: 'y-axis-count',
            data: this.taskBreakdown.map((s) => s.totalTasks || 0),
            order: 2
          }
        ]
      }
    }
  },
  watch: {
    selectedDepartment () {
      this.fetchData()
    }
  },
  mounted () {
    const today = new Date().toISOString().split('T')[0]
    const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0]
    this.dates = [weekAgo, today]
    this.fetchData()
  },
  methods: {
    async fetchData () {
      this.loading = true
      try {
        const [startDate, endDate] = this.sortedDates
        const dashboardQuery = { startDate, endDate }

        // ส่ง department ไป filter ที่ server (ถ้าไม่ใช่ All)
        if (this.selectedDepartment && this.selectedDepartment !== 'All') {
          dashboardQuery.department = this.selectedDepartment
        }

        const [dashboardRes] = await Promise.all([
          provider.getReportDashboard(dashboardQuery)
        ])

        const dashData = dashboardRes.data || {}
        this.kpi = dashData.kpi || {
          totalTasks: 0,
          completedTasks: 0,
          workingTasks: 0,
          totalQuantity: 0,
          totalDuration: 0,
          avgPiecesPerHour: 0
        }
        this.taskBreakdown = dashData.taskBreakdown || []
      } catch (err) {
        console.error('Dashboard fetch error:', err)
      } finally {
        this.loading = false
      }
    },
    formatDuration (ms) {
      if (!ms) return '-'
      const sec = Math.floor(ms / 1000)
      const h = Math.floor(sec / 3600)
      const m = Math.floor((sec % 3600) / 60)
      const s = sec % 60
      const parts = []
      if (h > 0) parts.push(`${h}h`)
      if (m > 0 || h > 0) parts.push(`${m}m`)
      parts.push(`${s}s`)
      return parts.join(' ')
    },
    handleDateChange () {
      // optional
    },
    confirmDate () {
      this.dateMenu = false
      this.fetchData()
    },
    exportCSV () {
      let csv = 'Employee,Task,Duration,Qty,Status\n'
      this.workLogs.forEach((row) => {
        csv += `${row.employee.firstName || ''},${row.taskType.name || ''},${this.formatDuration(row.duration)},${row.quantity || 0},${row.state}\n`
      })
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.setAttribute('download', 'warehouse_report.csv')
      link.click()
    }
  }
}
</script>

<style scoped>
.v-data-table >>> thead th {
  background-color: #FAFAFA !important;
  font-weight: 600 !important;
}
</style>
